/** The VAT engine: a shop's VAT profile, the VAT return, proof of transport and the global tax table. */
export default {
    settings: {
        title: 'VAT',
        intro: 'The facts the VAT engine decides from, as you or your tax adviser confirmed them. The engine never fills in a missing fact: an order it cannot decide goes to review.',
        modes: {
            originEligible: 'Dutch VAT: under the €10,000 EU threshold, no election',
            destinationRequired: 'Destination VAT: threshold exceeded',
            destinationElected: 'Destination VAT: elected (binding for 2 years)',
        },
        consumerVat: {
            nl: 'Dutch VAT: our EU sales to consumers stay under €10,000 a year',
            dest: "Their own country's VAT: over €10,000, or we chose to",
            unconfirmed: 'Not confirmed yet',
        },
        destinationReason: {
            required: 'We are over the €10,000 threshold',
            elected: 'We chose to (binding for 2 years)',
        },
        policy: {
            keepGross: 'The price on the site (your margin absorbs the rate difference)',
            keepNet: "The price at their country's VAT rate",
        },
        toasts: {
            draftStarted: 'Draft started',
            draftSaved: 'Draft saved',
            approved: 'Profile approved',
            engineOn: 'VAT engine on',
            engineOff: 'VAT engine off',
            draftDiscarded: 'Draft discarded',
        },
        confirmApprove: "Approve this profile? From now on it decides how this shop's sales are taxed, and it cannot be changed afterwards.",
        confirmEngineOn: 'Switch the VAT engine on? From now on orders are charged and shown the way the engine decides: VAT after the voucher, VAT on shipping, and incl. VAT on receipts. An order it cannot decide is refused.',
        confirmEngineOff: 'Switch the VAT engine off? New orders are taxed per product again.',
        confirmDiscard: 'Discard this draft?',
        engine: {
            title: 'Engine',
            onText: 'Orders are taxed by the VAT engine and shown incl. VAT.',
            offText: 'Orders are taxed per product, as before.',
            approveFirst: 'Approve a profile first',
            switchOff: 'Switch off',
            switchOn: 'Switch on',
        },
        inForce: {
            title: 'Profile in force',
            none: 'No approved profile yet. Start one below; the engine cannot tax an order without it.',
            version: 'Version',
            versionValue: '{version}, from {date}',
            seller: 'Seller',
            sellerValue: '{name}, ships from {country}',
            vatNumbers: 'VAT numbers',
            consumersAbroad: 'Consumers abroad',
            notConfirmed: 'Not confirmed',
            modeFrom: '(from {from})',
            modeFromUntil: '(from {from} until {until})',
            declaredThrough: 'Declared through',
            oss: 'OSS ({country}) from {date}',
            local: 'Local registration: {countries}',
            dutchOnly: 'Dutch VAT return only',
            approved: 'Approved',
            approvedBy: '{date} by {name}',
            change: 'Change profile',
            start: 'Start profile',
        },
        draft: {
            title: 'Draft version {version}',
            sellerName: "Seller's legal name",
            sellerNameHint: 'As on the invoice, e.g. Winkel B.V.',
            appliesFrom: 'Applies from',
            appliesFromHint: 'Never earlier than the moment it is approved.',
            vatNumbers: 'Your VAT numbers',
            vatNumbersHint: 'Your Dutch VAT number. It goes on invoices and is needed for 0% sales to businesses in other EU countries.',
            addVatNumber: 'Add VAT number',
            consumerVat: 'Where do consumers in other EU countries pay VAT?',
            consumerVatHint: 'Under €10,000 a year of sales to consumers in other EU countries (all countries together, excl. VAT, this year and last) you charge Dutch VAT. Count all your channels, not only this shop, and confirm with your adviser.',
            unconfirmedWarning: 'Until this is confirmed, checkout refuses orders from consumers in other EU countries. Dutch orders and businesses with a valid VAT number are not affected.',
            modeSummary: 'From {date} (change)',
            modeSummaryReference: 'From {date}, {reference} (change)',
            from: 'From',
            untilOptional: 'Until (optional)',
            referenceOptional: 'Reference (optional)',
            referencePlaceholder: "Adviser's letter",
            why: 'Why',
            reference: 'Reference',
            referencePlaceholderDestination: "Adviser's letter, election",
            howDeclare: 'How you declare it',
            howDeclareHint: "Through OSS (the EU one-stop shop, filed with the Dutch tax office), or a VAT registration in the customer's country.",
            ossCountry: 'OSS registered in',
            ossCountryPlaceholder: 'NL (empty: not registered)',
            ossFrom: 'OSS from',
            addLocal: 'Add local registration',
            policy: 'What a customer abroad pays',
            save: 'Save draft',
            approve: 'Approve',
            discard: 'Discard',
        },
        history: {
            title: 'History',
            version: 'Version',
            from: 'From',
            approved: 'Approved',
            inForce: 'in force',
        },
    },
    report: {
        title: 'VAT return',
        period: 'Period',
        quarter: 'Q{quarter} {year}',
        quarterRunning: 'Q{quarter} {year} (running)',
        perProduct: '{n} order in this period was taxed per product, not by the VAT engine, and is not in these figures. | {n} orders in this period were taxed per product, not by the VAT engine, and are not in these figures.',
        empty: 'No sales taxed by the VAT engine in this period.',
        nlReturn: 'Dutch VAT return',
        box: 'Box',
        base: 'Base',
        vat: 'VAT',
        oss: 'OSS (destination VAT)',
        local: 'Local VAT registrations',
        rate: 'Rate',
        icp: 'Intra-community listing (ICP)',
        icpHint: '0% sales to businesses in other EU countries, per buyer VAT number.',
        vatNumber: 'VAT number',
        giftCards: 'Gift cards sold: {amount}, outside the scope of VAT and declared nowhere.',
    },
    evidence: {
        title: 'Proof of transport',
        pieces: {
            vies: 'VIES check',
            invoice: 'Invoice',
            tracking: 'Tracking code',
            delivery: 'Delivery confirmation',
        },
        status: {
            complete: 'Proof complete',
            pending: 'Not shipped yet',
            missing: 'Proof missing',
            exception: 'Accepted as exception',
            notRequired: 'No proof needed',
        },
        panel: {
            missingCount: '{n} missing | {n} missing',
            intro: 'Sales at 0% to businesses in other EU countries. Each needs proof in the order file that the goods left the Netherlands.',
            order: 'Order',
            buyer: 'Buyer',
            proof: 'Proof',
            shipped: 'shipped {date}',
            paid: 'paid {date}',
            missing: 'Missing: {pieces}',
        },
        card: {
            intro: 'Sold at 0% to a business in another EU country. The order file must show the goods left the Netherlands.',
            deliveryRecorded: 'Delivery recorded',
            deliveryRemoved: 'Delivery confirmation removed',
            fileAdded: 'File added',
            fileRemoved: 'File removed',
            exceptionAccepted: 'Accepted as an exception',
            exceptionWithdrawn: 'Exception withdrawn',
            confirmClearDelivery: 'Remove the delivery confirmation?',
            confirmRemoveFile: 'Remove {name}?',
            theFile: 'the file',
            confirmWithdraw: 'Withdraw the exception? The proof counts as missing again.',
            vies: '{number}: {result}, {date}',
            viesConsultation: '{number}: {result}, {date}, consultation {consultation}',
            noVies: 'No VIES check on this order',
            invoice: '{number}, {date}',
            noInvoice: 'Send the invoice from the Invoice tab',
            noTracking: 'Add it under Shipment',
            delivered: 'Delivered {date}',
            deliveredReference: 'Delivered {date}, {reference}',
            noDelivery: 'Record it below once the carrier confirms delivery',
            deliveredLine: 'Delivered {date}.',
            deliveredLineReference: 'Delivered {date}, carrier reference {reference}.',
            recordedBy: 'Recorded by {name} {date}',
            change: 'Change',
            deliveryConfirmation: 'Delivery confirmation',
            deliveredOn: 'Delivered on',
            carrierReference: 'Carrier reference',
            carrierReferencePlaceholder: 'Track & trace, CMR number',
            note: 'Note (optional)',
            notePlaceholder: 'Signed for by …',
            recordDelivery: 'Record delivery',
            proofFile: 'Proof file',
            optional: '(optional)',
            replace: 'Replace',
            addFile: 'Add file',
            fileHint: "The carrier's proof of delivery or a signed CMR. PDF, JPG or PNG, at most 10 MB.",
            acceptedBy: 'Accepted without all proof by {name} {date}:',
            withdraw: 'Withdraw exception',
            acceptWithout: 'Accept without full proof…',
            why: 'Why is this sale accepted without all proof?',
            accept: 'Accept as exception',
        },
    },
    table: {
        title: 'Tax table',
        intro: 'VAT categories and the rate each country charges for them, for every shop. A rule is charged only after someone other than its author approves it.',
        treatments: {
            standard: 'Standard',
            reduced: 'Reduced',
            zeroRated: 'Zero rated (0%)',
            exempt: 'Exempt',
        },
        treatmentShort: {
            standard: 'standard',
            reduced: 'reduced',
            zeroRated: 'zero rated',
            exempt: 'exempt',
        },
        status: {
            draft: 'Awaiting approval',
            inForce: 'In force',
            scheduled: 'Scheduled',
            ended: 'Ended',
        },
        toasts: {
            categoryAdded: 'Category added',
            ruleAdded: 'Rule added; it counts once someone else approves it',
            ruleApproved: 'Rule approved',
            ruleEnded: 'Rule ended',
            draftDeleted: 'Draft deleted',
        },
        pickCategoryRate: 'Pick a category and a rate.',
        confirmApprove: 'Approve {country} {category} at {rate} from {date}? It will be charged on every shop that sells this category there.',
        promptEnd: 'End this rule on (YYYY-MM-DD, the first day it no longer applies):',
        confirmDelete: 'Delete this draft rule?',
        rules: {
            title: 'Rules',
            countryCategory: 'Country · category',
            rate: 'Rate',
            from: 'From',
            until: 'Until',
            source: 'Source',
            open: 'open',
            by: 'by {name}',
            byApproved: 'by {name}, approved by {approver}',
            migration: 'migration',
            approve: 'Approve',
            end: 'End',
            category: 'Category',
            treatment: 'Treatment',
            ratePercent: 'Rate %',
            sourcePlaceholder: 'Law article or official page',
            add: 'Add rule',
        },
        categories: {
            title: 'Categories',
            label: 'Label',
            contents: 'What belongs in it',
            codePlaceholder: 'food',
            labelPlaceholder: 'Food',
            add: 'Add category',
        },
    },
};
